import { Search, Filter, Users, MapPin, Briefcase, BarChart3, MessageSquare, Sparkles } from "lucide-react";

/** Mocked product UI snippets used inside FeatureSection gradient panels */

export const FeedbackInboxMock = () => (
  <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 sm:gap-4 max-w-5xl mx-auto min-w-0">
    <div className="bg-card rounded-2xl p-4 sm:p-5 mkt-shadow-card">
      <div className="text-sm font-bold text-foreground">Channels</div>
      <div className="mt-3 sm:mt-4 space-y-1.5 sm:space-y-2">
        {["All feedback", "Engineering", "Product", "Support", "Exit interviews"].map((c, i) => (
          <div key={c} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] sm:text-sm ${i === 0 ? "bg-[hsl(var(--mkt-primary)/0.10)] text-[hsl(var(--mkt-primary-deep))] font-semibold" : "text-foreground/70"}`}>
            <MessageSquare className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{c}</span>
            <span className="ml-auto text-xs opacity-60">{[124, 38, 22, 14, 6][i]}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-card rounded-2xl p-4 sm:p-6 mkt-shadow-card min-w-0">
      <div className="flex items-center justify-between mb-4 sm:mb-5 gap-2">
        <div className="text-sm sm:text-base font-bold truncate">Anonymous feedback · today</div>
        <div className="text-xs text-muted-foreground shrink-0">Live</div>
      </div>
      <div className="space-y-2.5 sm:space-y-3">
        {[
          { tag: "Risk", color: "bg-rose-100 text-rose-700", text: "Manager hasn't given any feedback in 3 months. Considering leaving." },
          { tag: "Praise", color: "bg-emerald-100 text-emerald-700", text: "The new flexible Friday policy actually changed my life. Thank you." },
          { tag: "Idea", color: "bg-blue-100 text-blue-700", text: "Could we run sprint retros async? Half the team is in PST." },
        ].map((r) => (
          <div key={r.text} className="border border-border rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.color}`}>{r.tag}</span>
              <span className="text-[11px] sm:text-xs text-muted-foreground">2 min ago · Engineering</span>
            </div>
            <div className="text-[13px] sm:text-sm text-foreground leading-snug">"{r.text}"</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const AIInsightsMock = () => (
  <div className="bg-card rounded-2xl p-4 sm:p-6 mkt-shadow-card max-w-3xl mx-auto">
    <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
      <div className="font-bold flex items-center gap-2 text-sm sm:text-base min-w-0">
        <span className="h-7 w-7 shrink-0 rounded-2xl bg-[hsl(var(--mkt-primary))] text-white inline-flex items-center justify-center">
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <span className="truncate">AI Theme Analysis</span>
      </div>
      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 shrink-0">Active</span>
    </div>
    <svg viewBox="0 0 600 200" className="w-full h-32 sm:h-44">
      <defs>
        <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(222 89% 58%)" stopOpacity="0.4"/>
          <stop offset="100%" stopColor="hsl(222 89% 58%)" stopOpacity="0"/>
        </linearGradient>
        <linearGradient id="g2" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(262 83% 58%)" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="hsl(262 83% 58%)" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d="M0 150 C 80 130, 130 80, 200 90 S 350 60, 420 70 S 540 40, 600 30 L 600 200 L 0 200 Z" fill="url(#g1)" />
      <path d="M0 150 C 80 130, 130 80, 200 90 S 350 60, 420 70 S 540 40, 600 30" stroke="hsl(222 89% 58%)" strokeWidth="2.5" fill="none" />
      <path d="M0 175 C 100 165, 180 160, 260 150 S 400 130, 480 125 S 580 110, 600 100 L 600 200 L 0 200 Z" fill="url(#g2)" />
      <path d="M0 175 C 100 165, 180 160, 260 150 S 400 130, 480 125 S 580 110, 600 100" stroke="hsl(262 83% 58%)" strokeWidth="2.5" fill="none" />
    </svg>
    <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-3">
      {[
        { l: "Burnout signals", v: "+18%", c: "text-rose-600" },
        { l: "Manager praise", v: "+42%", c: "text-emerald-600" },
        { l: "Tooling friction", v: "−9%", c: "text-foreground" },
      ].map((s) => (
        <div key={s.l} className="border border-border rounded-xl p-2.5 sm:p-3">
          <div className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{s.l}</div>
          <div className={`text-base sm:text-lg font-bold ${s.c}`}>{s.v}</div>
        </div>
      ))}
    </div>
  </div>
);

export const ChannelsMock = () => (
  <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 sm:gap-4 max-w-5xl mx-auto min-w-0">
    <div className="bg-card rounded-2xl p-4 sm:p-5 mkt-shadow-card">
      <div className="text-sm font-bold">Filters</div>
      <div className="mt-3 sm:mt-4 grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-3 text-[13px] sm:text-sm">
        {[
          { Icon: Users, label: "Department" },
          { Icon: MapPin, label: "Location" },
          { Icon: Briefcase, label: "Tenure" },
          { Icon: Filter, label: "Sentiment" },
          { Icon: BarChart3, label: "Risk score" },
        ].map(({ Icon, label }) => (
          <div key={label} className="flex items-center justify-between border border-border rounded-xl px-2.5 sm:px-3 py-2 sm:py-2.5 text-foreground/80 min-w-0">
            <span className="inline-flex items-center gap-1.5 sm:gap-2 min-w-0"><Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{label}</span></span>
            <span className="text-muted-foreground shrink-0">▾</span>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-card rounded-2xl p-4 sm:p-6 mkt-shadow-card flex flex-col items-center justify-center text-center">
      <div className="h-14 w-14 sm:h-20 sm:w-20 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[hsl(var(--mkt-primary))] to-[hsl(var(--mkt-purple))] inline-flex items-center justify-center text-white mb-4 sm:mb-5">
        <Search className="h-6 w-6 sm:h-8 sm:w-8" />
      </div>
      <div className="text-lg sm:text-xl font-bold">Ask SendFeedback AI</div>
      <div className="text-xs sm:text-sm text-muted-foreground mt-1 leading-snug">Surface 3 themes driving Eng turnover this quarter</div>
      <div className="mt-4 sm:mt-5 w-full max-w-md flex items-center gap-1.5 sm:gap-2 border border-border rounded-full pl-3 sm:pl-5 pr-1 sm:pr-1.5 py-1 sm:py-1.5 min-w-0">
        <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground shrink-0" />
        <input
          readOnly
          placeholder="Why are engineers churning?"
          className="flex-1 min-w-0 bg-transparent text-xs sm:text-sm outline-none truncate"
        />
        <button className="h-8 sm:h-9 px-2.5 sm:px-4 rounded-full bg-[hsl(var(--mkt-primary))] text-white text-[11px] sm:text-sm font-medium shrink-0">
          Ask AI
        </button>
      </div>
    </div>
  </div>
);

export const AnalyticsMock = () => (
  <div className="bg-card rounded-2xl sm:rounded-3xl p-4 sm:p-8 mkt-shadow-card max-w-3xl mx-auto">
    <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
      <div className="font-bold text-sm sm:text-base truncate">Engagement · last 14 days</div>
      <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 shrink-0">Active</span>
    </div>
    <svg viewBox="0 0 600 200" className="w-full h-36 sm:h-48">
      {[0, 50, 100, 150, 200].map((y) => (
        <line key={y} x1="0" x2="600" y1={y} y2={y} stroke="hsl(220 13% 91%)" />
      ))}
      <path d="M10 130 C 80 110, 130 80, 200 75 S 350 50, 420 45 S 540 30, 590 25" stroke="hsl(38 92% 55%)" strokeWidth="3" fill="none" />
      <path d="M10 160 C 80 150, 130 130, 200 120 S 350 95, 420 90 S 540 75, 590 70" stroke="hsl(262 83% 58%)" strokeWidth="3" fill="none" />
      <path d="M10 180 C 80 175, 130 170, 200 165 S 350 155, 420 150 S 540 145, 590 140" stroke="hsl(142 71% 45%)" strokeWidth="3" fill="none" />
    </svg>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 sm:mt-4 text-[11px] sm:text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-amber-500"/>Pulse score</span>
      <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-purple-500"/>Risk index</span>
      <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500"/>Praise volume</span>
    </div>
  </div>
);
